import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";
import { updateRegion, regionsRecord, clearState, fetchRegions } from "../../redux/Slices/RegionsSlice";
import { validateFieldLanguage } from "../../Utils/languageValidation";
import { fetchCities } from "../../redux/Slices/CitiesSlice";

const EditRegion = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    name_ar: "",
    name_en: "",
    city_id: "",
    status: ""
  });

  const [originalData, setOriginalData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { regions, isLoading: loading, error, success, record } = useSelector((state) => state.regions);
  const { cities } = useSelector((state) => state.cities);

  useEffect(() => {
    dispatch(clearState());
    dispatch(fetchCities());
    dispatch(regionsRecord(id));
    if (!regions || !regions.data || regions.data.length === 0) {
      dispatch(fetchRegions());
    }
  }, [dispatch, id, regions]);

  useEffect(() => {
    if (record) {
      const original = {
        name_ar: record?.name_ar || "",
        name_en: record?.name_en || "",
        city_id: record?.city_id || "",
        status: record?.status === "active" ? 1 : 0,
        shipping_price: record?.shipping_price || 0,
      };
      setOriginalData(original);
      setFormData(original);
    }
  }, [record]);

  // Check for changes
  useEffect(() => {
    if (originalData) {
      const hasFormChanges =
        formData.name_ar !== originalData.name_ar ||
        formData.name_en !== originalData.name_en ||
        formData.city_id !== originalData.city_id ||
        formData.status !== originalData.status ||
        formData.shipping_price !== originalData.shipping_price;

      setHasChanges(hasFormChanges);
    }
  }, [formData, originalData]);

  useEffect(() => {
    if (success) {
      toast.success("تم التحديث بنجاح");
      dispatch(clearState());
      navigate("/regions");
    }
    if (error) {
      toast.error("لم يتم التحديث");
      dispatch(clearState());
    }
  }, [success, error, dispatch, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // BUG-32 fix: التحقق من لغة الحقول
    const arError = validateFieldLanguage(formData.name_ar, "ar");
    if (arError) return toast.error(arError);
    const enError = validateFieldLanguage(formData.name_en, "en");
    if (enError) return toast.error(enError);

    // التحقق من عدم تكرار اسم المنطقة (عربي أو إنجليزي) لنفس المدينة - باستثناء المنطقة الحالية
    const isDuplicateAr = regions && Array.isArray(regions.data) && regions.data.some(
      (r) =>
        r.id !== Number(id) &&
        Number(r.city_id) === Number(formData.city_id) &&
        r.name_ar.trim().toLowerCase() === formData.name_ar.trim().toLowerCase()
    );
    if (isDuplicateAr) {
      return toast.error("اسم المنطقة بالعربي مكرر بالفعل لهذه المدينة");
    }

    const isDuplicateEn = regions && Array.isArray(regions.data) && regions.data.some(
      (r) =>
        r.id !== Number(id) &&
        Number(r.city_id) === Number(formData.city_id) &&
        r.name_en.trim().toLowerCase() === formData.name_en.trim().toLowerCase()
    );
    if (isDuplicateEn) {
      return toast.error("اسم المنطقة بالإنجليزي مكرر بالفعل لهذه المدينة");
    }

    dispatch(clearState());
    dispatch(updateRegion({ id, formData }));
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 9,
    border: "1.5px solid #e2e8f0",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    background: "#fff",
    transition: "border-color 0.2s, box-shadow 0.2s"
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    display: "block",
    marginBottom: 6,
    textAlign: "right"
  };

  const focusHandler = (e) => {
    e.target.style.borderColor = "var(--main-color)";
    e.target.style.boxShadow = "0 0 0 3px rgba(228, 109, 23, 0.1)";
  };

  const blurHandler = (e) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
  };

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>
      <style>{`
        .region-custom-select {
          width: 100% !important;
          padding: 10px 14px !important;
          padding-left: 40px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
          background-repeat: no-repeat !important;
          background-position: left 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .region-custom-select:focus {
          border-color: var(--main-color) !important;
          box-shadow: 0 0 0 3px rgba(228, 109, 23, 0.1) !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            🗺 تعديل تفاصيل المنطقة
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>تعديل تفاصيل وحالة المنطقة: {record?.name_ar ? `(${record.name_ar})` : ""}</p>
        </div>
        <Link to="/regions" className="text-decoration-none">
          <button style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 10, border: "1px solid #cbd5e1", background: "#fff", color: "#475569", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
            <FaArrowLeft size={12} /> العودة
          </button>
        </Link>
      </div>

      {/* ── Form Card ─────────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "24px 30px", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>اسم المنطقة (عربي) *</label>
              <input
                type="text"
                style={inputStyle}
                name="name_ar"
                value={formData.name_ar}
                onChange={handleInputChange}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="أدخل الاسم (عربي)"
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>اسم المنطقة (إنجليزي) *</label>
              <input
                type="text"
                style={inputStyle}
                name="name_en"
                value={formData.name_en}
                onChange={handleInputChange}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="أدخل الاسم (إنجليزي)"
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>المدينة التابعة لها *</label>
              <select
                className="region-custom-select"
                name="city_id"
                value={formData.city_id ? String(formData.city_id) : ""}
                onChange={handleInputChange}
                required
              >
                <option value="" disabled>اختر مدينة</option>
                {cities && cities?.data?.length > 0 && cities?.data?.map((city) => (
                  <option key={city.id} value={String(city.id)}>{city.name_ar}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>حالة التفعيل *</label>
              <select
                className="region-custom-select"
                name="status"
                value={formData.status !== undefined && formData.status !== null ? String(formData.status) : ""}
                onChange={handleInputChange}
                required
              >
                <option value="" disabled>اختر الحالة</option>
                <option value="0">غير مفعل</option>
                <option value="1">مفعل</option>
              </select>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid #f1f5f9", marginTop: 20, paddingTop: 20 }}>
            <button type="button" onClick={() => navigate("/regions")} style={{ padding: "9px 22px", borderRadius: 9, border: "1px solid #cbd5e1", background: "#fff", color: "#475569", fontWeight: 700, cursor: "pointer" }}>إلغاء</button>
            <button type="submit" disabled={loading || !hasChanges} style={{ padding: "9px 28px", borderRadius: 9, border: "none", background: hasChanges ? "linear-gradient(135deg,var(--main-color),var(--main-light))" : "#cbd5e1", color: "#fff", fontWeight: 700, cursor: hasChanges ? "pointer" : "not-allowed" }}>
              {loading ? 'جاري الحفظ...' : 'حفظ التعديلات'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRegion;
